import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Activity, Circle, Loader2, LogIn, UserPlus, Shield, FileText, CreditCard } from 'lucide-react';
import { AdminLayout } from './AdminLayout';
import { getAdminActivity } from '@/lib/api';

const ACTION_ICONS = {
  login: LogIn,
  register: UserPlus,
  llc_setup: Shield,
  contract_sign: FileText,
  payment: CreditCard,
};

const ACTION_COLORS = {
  login: 'text-rtb-green',
  register: 'text-blue-400',
  llc_setup: 'text-rtb-purple',
  contract_sign: 'text-yellow-400',
  payment: 'text-emerald-400',
};

export default function AdminActivity() {
  const [activity, setActivity] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getAdminActivity().then(setActivity).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const onlineAdmins = activity.filter(a => a.action === 'admin_login' && a.is_online);

  return (
    <AdminLayout title="Activity Log">
      {/* Online Admins */}
      <div className="p-5 bg-white/[0.04] border border-white/[0.06] rounded-xl mb-6" data-testid="online-admins">
        <h3 className="font-oswald text-base font-bold text-white mb-4">Currently Online</h3>
        {onlineAdmins.length === 0 ? (
          <p className="text-white/30 text-sm">No other admins currently online</p>
        ) : (
          <div className="flex flex-wrap gap-3">
            {onlineAdmins.map((a, i) => (
              <div key={i} className="flex items-center gap-2 px-3 py-2 bg-rtb-green/5 border border-rtb-green/10 rounded-lg">
                <Circle className="w-2.5 h-2.5 text-rtb-green fill-rtb-green animate-pulse" />
                <span className="text-sm text-white/80">{a.admin_name || a.username}</span>
                <span className="text-white/20 text-xs">({a.role})</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Activity Feed */}
      <div className="p-5 bg-white/[0.04] border border-white/[0.06] rounded-xl" data-testid="activity-feed">
        <h3 className="font-oswald text-base font-bold text-white mb-4">Recent Activity</h3>
        {loading ? (
          <div className="flex items-center justify-center h-32"><Loader2 className="w-6 h-6 text-rtb-purple animate-spin" /></div>
        ) : activity.length === 0 ? (
          <p className="text-white/30 text-center py-12 text-sm">No activity recorded yet</p>
        ) : (
          <div className="space-y-1">
            {activity.map((a, i) => {
              const Icon = ACTION_ICONS[a.action] || Activity;
              const color = ACTION_COLORS[a.action] || 'text-white/40';
              return (
                <motion.div key={i} initial={{ opacity: 0, x: -5 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.02 }}
                  className="flex items-center gap-4 px-4 py-3 hover:bg-white/[0.02] rounded-lg transition-colors"
                >
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center bg-white/[0.04] ${color}`}>
                    <Icon className="w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-white/70">{a.description}</p>
                  </div>
                  <span className="text-white/20 text-xs whitespace-nowrap">{new Date(a.created_at).toLocaleString()}</span>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
